const ALLOWED_DECISIONS = new Set(["BUY", "HOLD", "WATCH", "SELL"]);
const CANONICAL_PORTFOLIO_CURRENCY = "USD";
const EMPTY_CURRENCY_CHECK = Object.freeze({
  hasMismatch: false,
  declaredCurrency: CANONICAL_PORTFOLIO_CURRENCY,
  inconsistentFields: [],
});

export const CLASSIFICATION_STATES = Object.freeze([
  "checking",
  "live",
  "partial",
  "empty",
  "error",
  "unauthorized",
  "timeout",
  "unavailable",
]);

function numberOr(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

// A backend pnlPct/pnlPercent field has, in production, arrived in two
// incompatible units for different rows of the SAME response — a real
// incident: one position reported -0.1291 (a decimal ratio, i.e. roughly
// minus thirteen percent)
// while sibling rows reported already-percentage-point values like -2.81.
// The frontend has no reliable way to detect which unit a given row used
// by inspecting that field alone. Whenever both entry and current price
// are real, non-zero numbers, the percentage is instead derived directly
// from them — the same unambiguous formula checkPnlConsistency() already
// uses to validate the raw field — so the display can never be off by a
// factor of 100 regardless of which unit convention the backend used for
// that row. The raw field is used only as a fallback when price data
// itself is unavailable (never as the primary source once real prices
// exist).
function derivePnlPct(entryPrice, currentPrice, rawPnlPct) {
  if (typeof entryPrice === "number" && Number.isFinite(entryPrice) && entryPrice !== 0 && typeof currentPrice === "number" && Number.isFinite(currentPrice)) {
    return ((currentPrice - entryPrice) / entryPrice) * 100;
  }
  return rawPnlPct;
}

function nullableNumber(...values) {
  for (const value of values) {
    if (value === null || value === undefined || value === "") continue;
    const number = Number(value);
    if (Number.isFinite(number)) return number;
  }
  return null;
}

function normalizeDecision(value) {
  const decision = String(value || "HOLD").toUpperCase();
  return ALLOWED_DECISIONS.has(decision) ? decision : "HOLD";
}

function cloneCurrencyCheck() {
  return {
    hasMismatch: EMPTY_CURRENCY_CHECK.hasMismatch,
    declaredCurrency: EMPTY_CURRENCY_CHECK.declaredCurrency,
    inconsistentFields: [],
  };
}

function resolvePortfolioCurrency() {
  return CANONICAL_PORTFOLIO_CURRENCY;
}

export function checkPnlConsistency({ entryPrice, currentPrice, quantity, pnlPct }) {
  if (
    entryPrice === null || entryPrice === undefined ||
    currentPrice === null || currentPrice === undefined ||
    quantity === null || quantity === undefined ||
    pnlPct === null || pnlPct === undefined
  ) {
    return null;
  }
  const parsedEntry = Number(entryPrice);
  const parsedCurrent = Number(currentPrice);
  const parsedQuantity = Number(quantity);
  const parsedPnlPct = Number(pnlPct);
  if (
    !Number.isFinite(parsedEntry) || parsedEntry === 0 ||
    !Number.isFinite(parsedCurrent) ||
    !Number.isFinite(parsedQuantity) ||
    !Number.isFinite(parsedPnlPct)
  ) {
    return null;
  }
  // A deviation above 1 percentage point is large enough to be visible and
  // should be surfaced instead of silently "fixing" either value.
  const expectedPnlPct = ((parsedCurrent - parsedEntry) / parsedEntry) * 100;
  return Math.abs(expectedPnlPct - parsedPnlPct) > 1
    ? "PnL-% weicht von Kursberechnung ab"
    : null;
}

/**
 * Classifies the status of a portfolio endpoint response.
 * Implements fail-closed validation: HTTP 200 alone does NOT mean "live".
 *
 * Returns an object with:
 * {
 *   status: "live" | "partial" | "empty" | "error" | "unauthorized" | "timeout" | "unavailable" | "checking"
 *   isLive: boolean
 *   reason: string
 *   httpStatus: number
 *   backendSuccess: boolean | null
 *   dataStatus: string | null
 *   errorCode: string | null
 *   partialErrorCount: number
 *   checkedAt: ISO string
 * }
 */
export function classifyPortfolioResponse(response) {
  const checkedAt = new Date().toISOString();
  
  // Missing or non-object response
  if (!response || typeof response !== "object") {
    return {
      status: "error",
      isLive: false,
      reason: "Response is not an object",
      httpStatus: null,
      backendSuccess: null,
      dataStatus: null,
      errorCode: null,
      partialErrorCount: 0,
      checkedAt,
    };
  }

  if (response.isLoading === true || response.loading === true || response.status === "checking") {
    return {
      status: "checking",
      isLive: false,
      reason: "Request in progress",
      httpStatus: null,
      backendSuccess: null,
      dataStatus: null,
      errorCode: null,
      partialErrorCount: 0,
      checkedAt,
    };
  }

  const httpStatus = typeof response.status === "number" ? response.status : null;
  
  // Extract payload carefully
  // Check if response has a wrapping data object with nested data
  let payload;
  if (response.data && typeof response.data === "object") {
    // If response.data exists and has a data field, unwrap it
    if ("data" in response.data) {
      payload = response.data.data;
    } else {
      payload = response.data;
    }
  } else if ("data" in response) {
    payload = response.data;
  } else {
    // Otherwise treat response itself as payload
    payload = response;
  }

  // Portfolio-Twin responses nest the actual snapshot under a `portfolioTwin`
  // key (`{ success: true, portfolioTwin: {...} }`); unwrap it so downstream
  // "does this payload contain twin data" checks look at the real fields.
  if (payload && typeof payload === "object" && payload.portfolioTwin && typeof payload.portfolioTwin === "object") {
    payload = payload.portfolioTwin;
  }
  
  // HTTP-level error status codes
  if (httpStatus === 401) {
    return {
      status: "unauthorized",
      isLive: false,
      reason: "Unauthorized (HTTP 401)",
      httpStatus,
      backendSuccess: null,
      dataStatus: null,
      errorCode: null,
      partialErrorCount: 0,
      checkedAt,
    };
  }

  if (httpStatus === 403) {
    return {
      status: "unauthorized",
      isLive: false,
      reason: "Forbidden (HTTP 403)",
      httpStatus,
      backendSuccess: null,
      dataStatus: null,
      errorCode: null,
      partialErrorCount: 0,
      checkedAt,
    };
  }

  // fix/pnl-dossier-mapping-and-live-truth: a 429 means the endpoint exists
  // and works, it is only rate-limited right now — the generic 4xx bucket
  // below used to fold this into "unavailable" ("NICHT DEPLOYED" in the
  // UI), which is a factually wrong label for a route that is live but
  // throttled. Real production incident: /api/admin/alerts/summary
  // returning 429 (apparently caught by an admin-write rate limit that
  // should not apply to a read-only route — a backend-side config issue,
  // out of scope here) was shown as "not deployed".
  if (httpStatus === 429) {
    return {
      status: "rate_limited",
      isLive: false,
      reason: "Rate limited (HTTP 429)",
      httpStatus,
      backendSuccess: null,
      dataStatus: null,
      errorCode: null,
      partialErrorCount: 0,
      checkedAt,
    };
  }

  if (httpStatus === 408 || httpStatus === 504) {
    return {
      status: "timeout",
      isLive: false,
      reason: `Request timeout (HTTP ${httpStatus})`,
      httpStatus,
      backendSuccess: null,
      dataStatus: null,
      errorCode: null,
      partialErrorCount: 0,
      checkedAt,
    };
  }

  if (httpStatus === 502 || httpStatus === 503) {
    return {
      status: "unavailable",
      isLive: false,
      reason: `Service unavailable (HTTP ${httpStatus})`,
      httpStatus,
      backendSuccess: null,
      dataStatus: null,
      errorCode: null,
      partialErrorCount: 0,
      checkedAt,
    };
  }

  if (httpStatus && httpStatus >= 400 && httpStatus < 500 && httpStatus !== 401 && httpStatus !== 403) {
    return {
      status: "unavailable",
      isLive: false,
      reason: `HTTP error ${httpStatus}`,
      httpStatus,
      backendSuccess: null,
      dataStatus: null,
      errorCode: null,
      partialErrorCount: 0,
      checkedAt,
    };
  }

  if (httpStatus && httpStatus >= 500) {
    return {
      status: "error",
      isLive: false,
      reason: `Server error (HTTP ${httpStatus})`,
      httpStatus,
      backendSuccess: null,
      dataStatus: null,
      errorCode: null,
      partialErrorCount: 0,
      checkedAt,
    };
  }

  if (response.error || (httpStatus !== null && (httpStatus === 0 || httpStatus < 0))) {
    return {
      status: "unavailable",
      isLive: false,
      reason: response.error || "Network or fetch error",
      httpStatus,
      backendSuccess: null,
      dataStatus: null,
      errorCode: response.error || null,
      partialErrorCount: 0,
      checkedAt,
    };
  }

  // HTTP 2xx responses (or no status code = assume 200) require further validation
  const is2xx = httpStatus === null || (httpStatus >= 200 && httpStatus < 300);
  if (is2xx) {
    // Get wrapping data for checking success/error fields
    const wrappingData = response.data && typeof response.data === "object" ? response.data : {};
    
    // Check for backend-level success field
    const backendSuccess = wrappingData?.success !== undefined ? wrappingData.success : payload?.success;
    const rawPartialErrors = wrappingData?.partialErrors || payload?.partialErrors;
    if (backendSuccess === false) {
      return {
        status: "error",
        isLive: false,
        reason: 'Backend returned success: false',
        httpStatus,
        backendSuccess: false,
        dataStatus: wrappingData?.dataStatus || payload?.dataStatus || null,
        errorCode: wrappingData?.error || payload?.error || null,
        partialErrorCount: Array.isArray(rawPartialErrors) ? rawPartialErrors.length : 0,
        checkedAt,
      };
    }

    // Check for dataStatus field
    const dataStatus = String(wrappingData?.dataStatus || payload?.dataStatus || "").toLowerCase();
    if (dataStatus === "error" || dataStatus === "failed") {
      return {
        status: "error",
        isLive: false,
        reason: `dataStatus is "${dataStatus}"`,
        httpStatus,
        backendSuccess: backendSuccess ?? null,
        dataStatus,
        errorCode: wrappingData?.error || payload?.error || null,
        partialErrorCount: Array.isArray(rawPartialErrors) ? rawPartialErrors.length : 0,
        checkedAt,
      };
    }

    // Check for partial errors
    const partialErrors = Array.isArray(rawPartialErrors) ? rawPartialErrors : [];
    if (partialErrors.length > 0) {
      // If there are partial errors, classify as "partial" if there's some data,
      // or "error" if there's no usable data
      const holdings = wrappingData?.holdings || payload?.holdings || [];
      const hasUsableData = Array.isArray(holdings) && holdings.length > 0;
      if (!hasUsableData) {
        return {
          status: "error",
          isLive: false,
          reason: `${partialErrors.length} partial error(s) and no usable holdings`,
          httpStatus,
          backendSuccess: backendSuccess ?? null,
          dataStatus,
          errorCode: null,
          partialErrorCount: partialErrors.length,
          checkedAt,
        };
      }
      return {
        status: "partial",
        isLive: false,
        reason: `${partialErrors.length} partial error(s) but some data present`,
        httpStatus,
        backendSuccess: backendSuccess ?? null,
        dataStatus,
        errorCode: null,
        partialErrorCount: partialErrors.length,
        checkedAt,
      };
    }

    // Check for empty/null payload
    if (payload == null) {
      const errorCode = wrappingData?.error || payload?.error;
      return {
        status: "empty",
        isLive: false,
        reason: errorCode ? `data is null with error code "${errorCode}"` : "data is null",
        httpStatus,
        backendSuccess: backendSuccess ?? null,
        dataStatus,
        errorCode,
        partialErrorCount: 0,
        checkedAt,
      };
    }

    if (typeof payload === "object" && !Array.isArray(payload) && Object.keys(payload).length === 0) {
      return {
        status: "empty",
        isLive: false,
        reason: "payload is empty object",
        httpStatus,
        backendSuccess: backendSuccess ?? null,
        dataStatus,
        errorCode: null,
        partialErrorCount: 0,
        checkedAt,
      };
    }

    if (Array.isArray(payload) && payload.length === 0) {
      return {
        status: "empty",
        isLive: false,
        reason: "payload is empty array",
        httpStatus,
        backendSuccess: backendSuccess ?? null,
        dataStatus,
        errorCode: null,
        partialErrorCount: 0,
        checkedAt,
      };
    }

    // Check for payload with data but key fields missing
    if (payload && typeof payload === "object") {
      // For holdings-based portfolios, require holdings array
      if ("holdings" in payload && !Array.isArray(payload.holdings)) {
        return {
          status: "error",
          isLive: false,
          reason: "holdings field is not an array",
          httpStatus,
          backendSuccess: backendSuccess ?? null,
          dataStatus,
          errorCode: null,
          partialErrorCount: 0,
          checkedAt,
        };
      }

      // If there's a holdings array but it's empty, this is empty or degraded
      if (Array.isArray(payload.holdings) && payload.holdings.length === 0) {
        return {
          status: "empty",
          isLive: false,
          reason: "holdings array is empty",
          httpStatus,
          backendSuccess: backendSuccess ?? null,
          dataStatus,
          errorCode: null,
          partialErrorCount: 0,
          checkedAt,
        };
      }

      // For twin-type responses, check for portfolio data
      if ("totalAllocatedEur" in payload || "virtualPositions" in payload || "budget" in payload || "openPositions" in payload || "openCount" in payload) {
        // Portfolio Twin or similar - check if it has meaningful data
        const hasPositions = (Array.isArray(payload.virtualPositions) && payload.virtualPositions.length > 0) ||
                             (Array.isArray(payload.holdings) && payload.holdings.length > 0) ||
                             (Array.isArray(payload.openPositions) && payload.openPositions.length > 0) ||
                             (Array.isArray(payload.positions) && payload.positions.length > 0);

        if (hasPositions || payload.budget != null || payload.totalAllocatedEur != null || payload.openCount != null) {
          return {
            status: "live",
            isLive: true,
            reason: "Valid portfolio data present",
            httpStatus,
            backendSuccess: backendSuccess ?? true,
            dataStatus,
            errorCode: null,
            partialErrorCount: 0,
            checkedAt,
          };
        }
      }

      // Generic payload with some content
      return {
        status: "live",
        isLive: true,
        reason: "Valid payload present",
        httpStatus,
        backendSuccess: backendSuccess ?? true,
        dataStatus,
        errorCode: null,
        partialErrorCount: 0,
        checkedAt,
      };
    }

    // Default for 2xx with some content
    return {
      status: "live",
      isLive: true,
      reason: "HTTP 2xx with non-null payload",
      httpStatus,
      backendSuccess: backendSuccess ?? true,
      dataStatus,
      errorCode: null,
      partialErrorCount: 0,
      checkedAt,
    };
  }

  // Fallback for other status codes
  return {
    status: "error",
    isLive: false,
    reason: `Unexpected HTTP status ${httpStatus}`,
    httpStatus,
    backendSuccess: null,
    dataStatus: null,
    errorCode: null,
    partialErrorCount: 0,
    checkedAt,
  };
}

export function normalizeDemoPortfolioResponse(response, templates = []) {
  // First, classify the response status
  const classification = classifyPortfolioResponse(response);
  
  const payload = response?.data?.data ?? response?.data ?? response ?? null;
  const holdings = Array.isArray(payload?.holdings) ? payload.holdings : [];
  
  // Detect currency inconsistencies
  const currencyCheck = cloneCurrencyCheck();
  
  // The audited Railway contract retains legacy `*Eur` field names while the
  // canonical virtual_positions values are denominated in USD.
  const currency = resolvePortfolioCurrency();
  const templateBySymbol = new Map(templates.map((item) => [String(item.symbol || "").toUpperCase(), item]));
  
  // Only extract positions if the response is classified as "live"
  const positions = classification.isLive
    ? holdings
        .filter((holding) => holding && typeof holding.symbol === "string")
        .map((holding) => {
          const symbol = holding.symbol.trim().toUpperCase();
          const template = templateBySymbol.get(symbol) || {};
          const holdingEntryPrice = nullableNumber(holding.entryPrice);
          const holdingCurrentPrice = nullableNumber(holding.currentPrice);
          // Backend docs and live fixtures define pnlPercent/unrealizedPnlPct as
          // percentage points already (e.g. -2.81 means -2.81%, not -281%) —
          // but see derivePnlPct()'s doc comment: a real backend row can send
          // a decimal ratio instead, so the price-derived value is preferred
          // whenever both prices are real.
          const pnlPct = numberOr(derivePnlPct(holdingEntryPrice, holdingCurrentPrice, holding.pnlPercent ?? holding.pnlPct ?? holding.unrealizedPnlPct), 0);
          const score = numberOr(holding.hqsScoreAtEntry ?? holding.hqsScore ?? holding.score, 0);
          const dataStatus = String(holding.dataStatus || "unknown").toLowerCase();
           
          // Detect legacy positions - missing lineage/proof
          const hasForecasting = holding.forecastId || holding.forecastTimestamp || holding.timeWindow || holding.modelVersion;
          const hasDecision = holding.decisionId || holding.decisionChainId || holding.agentName;
          const hasExecution = holding.executionId || holding.executionTimestamp || holding.orderTime;
          const isLegacy = !hasForecasting || !hasDecision || !hasExecution;
          const legacyMarker = isLegacy ? "Legacy-Position – Forecast-/Entscheidungsnachweis unvollständig" : "";
           
          return {
            ...template,
            currency,
            symbol,
            name: template.name || symbol,
            sector: template.sector || String(holding.portfolioBucket || "Live portfolio"),
            status: normalizeDecision(holding.decisionType || holding.decision || holding.status),
            scope: "active",
            entry: numberOr(holding.entryPrice, 0),
            price: numberOr(holding.currentPrice, numberOr(holding.entryPrice, 0)),
            quantity: numberOr(holding.quantity, 0),
            weight: numberOr(holding.allocatedPct, 0),
            pnlPct,
            pnl: numberOr(holding.pnlEur ?? holding.pnl, 0),
            score,
            confidence: numberOr(holding.confidence ?? holding.reliabilityScore, score),
            risk: dataStatus === "red" || String(holding.freshness).toLowerCase() === "stale" ? "ELEVATED" : dataStatus === "yellow" ? "MEDIUM" : "LOW",
            target: numberOr(holding.targetPrice, 0),
            stop: numberOr(holding.stopPrice, 0),
            heldDays: holding.openedAt ? Math.max(0, Math.floor((Date.now() - new Date(holding.openedAt).getTime()) / 86_400_000)) : 0,
            forecast: template.forecast || [0, 0, 0],
            spark: template.spark || [45, 45, 45, 45, 45, 45, 45, 45],
            factors: template.factors || { Quality: score, Momentum: score, Value: score, Stability: score, Sentiment: score },
            consensus: String(holding.decisionStatus || template.consensus || "Live-Entscheidung aus Railway"),
            thesis: template.thesis || "Die Detailbegründung wird aus dem Manager-Agent- und Evidence-Pfad geladen.",
            catalyst: template.catalyst || "Live Signal- und Outcome-Daten",
            governor: template.governor || String(holding.conviction || "Governor-Status über Evidence-Pfad"),
            isLegacy,
            legacyMarker,
            live: true,
            raw: holding,
          };
        })
    : [];

  return {
    positions,
    hasLiveData: classification.isLive,
    classification,
    currencyCheck,
    dataStatus: String(payload?.dataStatus || (positions.length ? "live" : "empty")),
    freshness: String(payload?.freshnessMetadata?.freshness || payload?.freshness || "unknown"),
    source: String(payload?.freshnessMetadata?.source || payload?.source || "unknown"),
    currency,
    totals: {
      allocated: numberOr(payload?.totalAllocatedEur ?? payload?.totalAllocated, positions.reduce((sum, item) => sum + item.entry * item.quantity, 0)),
      value: numberOr(payload?.totalCurrentValueEur ?? payload?.totalCurrentValue, positions.reduce((sum, item) => sum + item.price * item.quantity, 0)),
      pnl: numberOr(payload?.totalPnlEur ?? payload?.totalPnl, positions.reduce((sum, item) => sum + item.pnl, 0)),
      pnlPct: numberOr(payload?.totalPnlPercent, 0),
      cash: nullableNumber(payload?.cashEur, payload?.cash, payload?.cashBalance, payload?.currentCash, payload?.availableCash),
    },
    raw: payload,
  };
}

/**
 * Normalize portfolio-twin endpoint response with fail-closed classification
 * Displays ONLY real data from the endpoint, NEVER demo/fallback values
 * Returns positions with full lineage information and legacy detection
 */
const LINEAGE_FIELD_LABELS = Object.freeze([
  ["forecastId", "Forecast-ID"],
  ["forecastTimestamp", "Forecast-Zeitpunkt"],
  ["timeWindow", "Horizont"],
  ["agentName", "Committee-/Agentenname"],
  ["admissionStatus", "Admission-Status"],
  ["decisionId", "Decision-ID"],
  ["chainRunId", "Chain-Run-ID"],
  ["executionId", "Execution-ID"],
  ["modelVersion", "Modellversion"],
  ["dataLineage", "Daten-Lineage"],
]);

function nullableString(value) {
  if (value === null || value === undefined || value === "") return null;
  return String(value);
}

/**
 * Extracts the Portfolio-Twin payload from any of the shapes the production
 * endpoint (or an already-unwrapped fetch helper) may hand us:
 *   - response.data.portfolioTwin   (raw axios-style wrapper)
 *   - response.portfolioTwin        (already unwrapped once)
 *   - response.data.data.portfolioTwin (double-wrapped admin proxy)
 *   - an already-normalized twin object itself (has budget/openCount/etc.)
 * Never falls back to demo-portfolio or any other endpoint.
 */
function extractPortfolioTwinPayload(response) {
  const candidates = [
    response?.data?.data?.portfolioTwin,
    response?.data?.portfolioTwin,
    response?.portfolioTwin,
    response?.data?.data,
    response?.data,
    response,
  ];
  for (const candidate of candidates) {
    if (candidate && typeof candidate === "object" && !Array.isArray(candidate)) {
      // A candidate only counts as the twin payload once it actually carries
      // twin-shaped fields — otherwise we'd accidentally treat the outer
      // envelope ({ success: true, portfolioTwin: {...} }) as the payload.
      const hasTwinShape = [
        "budget",
        "openPositions",
        "closedPositions",
        "openCount",
        "closedCount",
        "totalAllocatedEur",
        "totalAllocated",
        "positions",
      ].some((key) => key in candidate);
      if (hasTwinShape) return candidate;
    }
  }
  return {};
}

/**
 * Normalize the production Portfolio-Twin response
 * (`GET /api/admin/portfolio-twin`).
 *
 * Displays ONLY real data from the endpoint, NEVER demo/fallback values.
 * Supports both the direct/nested payload shapes documented in the backend
 * contract and legacy field aliases, without ever inventing a value:
 *   - vorhandener valider Wert  -> übernehmen
 *   - tatsächliche "0"          -> als echte Null übernehmen
 *   - Feld fehlt / ist null     -> null (UI zeigt "Nicht verfügbar")
 */
export function normalizePortfolioTwinResponse(response) {
  const classification = classifyPortfolioResponse(response);

  // Fail-closed: No data for non-live states
  if (!classification.isLive) {
    return {
      classification,
      hasLiveData: false,
      positions: [],
      closedPositions: [],
      totals: {
        budget: null,
        allocated: null,
        deployedCapitalPct: null,
        cash: null,
        pnl: null,
        pnlRealized: null,
        total: null,
        openPositions: null,
        closedPositions: null,
        currentDrawdownPct: null,
        maxDrawdownPct: null,
        winRate: null,
        hitRate: null,
      },
      currencyCheck: cloneCurrencyCheck(),
      meta: {
        lastUpdate: null,
        currency: null,
        dataStatus: classification.status,
        twinMaturity: null,
        priceSource: null,
      },
    };
  }

  const payload = extractPortfolioTwinPayload(response);
  const currencyCheck = cloneCurrencyCheck();
  const portfolioCurrency = resolvePortfolioCurrency();

  const mapPosition = (pos) => {
    const lineage = {
      forecastId: nullableString(pos.forecastId),
      forecastTimestamp: nullableString(pos.forecastTimestamp),
      timeWindow: nullableString(pos.timeWindow),
      agentName: nullableString(pos.agentName),
      admissionStatus: nullableString(pos.admissionStatus),
      decisionId: nullableString(pos.decisionId),
      chainRunId: nullableString(pos.chainRunId),
      executionId: nullableString(pos.executionId),
      modelVersion: nullableString(pos.modelVersion),
      dataLineage: nullableString(pos.dataLineage),
    };
    const missingLineageFields = LINEAGE_FIELD_LABELS
      .filter(([key]) => lineage[key] === null)
      .map(([, label]) => label);
    const entryPrice = nullableNumber(pos.entryPrice);
    const currentPrice = nullableNumber(pos.currentPrice);
    const quantity = nullableNumber(pos.quantity);
    const rawDecision = nullableString(pos.decisionType ?? pos.decision ?? pos.status);
    const riskSource = nullableString(pos.dataStatus ?? pos.freshness);
    const openedAt = nullableString(pos.openedAt ?? pos.entryTimestamp ?? pos.openedTimestamp ?? pos.entryDate);
    const openedAtTimestamp = openedAt === null ? Number.NaN : Date.parse(openedAt);
    // Backend docs and live fixtures define pnlPercent/pnlPct as percentage
    // points already (for example -2.81 means -2.81%, not -281%) — but see
    // derivePnlPct()'s doc comment: a real backend row can send a decimal
    // ratio instead (a production incident, not a hypothetical), so the
    // price-derived value is used whenever both entryPrice/currentPrice are
    // real, and the raw field is only a fallback when price data is missing.
    const rawPnlPct = nullableNumber(pos.pnlPercent, pos.pnlPct, pos.unrealizedPnlPct);
    const pnlPct = derivePnlPct(entryPrice, currentPrice, rawPnlPct);
    const isLegacy = !(lineage.forecastId && lineage.decisionId && lineage.executionId);
    return {
      symbol: pos.symbol,
      quantity,
      entryPrice,
      currentPrice,
      allocatedPct: nullableNumber(pos.allocatedPct, pos.weightPct, pos.positionWeightPct, pos.weight),
      pnl: nullableNumber(pos.pnl, pos.pnlEur, pos.unrealizedPnlEur),
      pnlPct,
      score: nullableNumber(pos.hqsScoreAtEntry, pos.hqsScore, pos.score),
      confidence: nullableNumber(pos.confidence, pos.reliabilityScore),
      status: rawDecision === null ? null : normalizeDecision(rawDecision),
      riskLevel: riskSource === null
        ? null
        : (riskSource.toLowerCase() === "red" || riskSource.toLowerCase() === "stale"
            ? "ELEVATED"
            : riskSource.toLowerCase() === "yellow"
              ? "MEDIUM"
              : "LOW"),
      openedAt,
      heldDays: Number.isFinite(openedAtTimestamp)
        ? Math.max(0, Math.floor((Date.now() - openedAtTimestamp) / 86_400_000))
        : null,
      targetPrice: nullableNumber(pos.targetPrice, pos.target),
      stopPrice: nullableNumber(pos.stopPrice, pos.stop),
      currency: nullableString(pos.currency || pos.currencyCode) ?? portfolioCurrency,
      pnlConsistencyWarning: checkPnlConsistency({ entryPrice, currentPrice, quantity, pnlPct }),
      ...lineage,
      isLegacy,
      missingLineageFields,
      legacyMarker: isLegacy ? "Legacy-Position – Forecast-/Entscheidungsnachweis unvollständig" : "",
    };
  };

  // Production contract uses `openPositions` as the array of currently open
  // positions; `positions` is kept as a legacy alias for older payload shapes.
  const openPositionsArray = Array.isArray(payload.openPositions)
    ? payload.openPositions
    : Array.isArray(payload.positions)
      ? payload.positions
      : [];
  const closedPositionsArray = Array.isArray(payload.closedPositions) ? payload.closedPositions : [];

  const positions = openPositionsArray
    .filter((pos) => pos && typeof pos === "object")
    .map(mapPosition);
  const closedPositions = closedPositionsArray
    .filter((pos) => pos && typeof pos === "object")
    .map(mapPosition);

  // openCount/closedCount are explicit counters from the backend and must be
  // preferred over deriving a count from the (possibly not-yet-populated)
  // array, since the backend may report a count without shipping the array.
  const openCount = nullableNumber(payload.openCount, payload.openPositionsCount) ?? (positions.length || null);
  const closedCount = nullableNumber(payload.closedCount, payload.closedPositionsCount) ?? (closedPositions.length || null);

  return {
    classification,
    hasLiveData: true,
    positions,
    closedPositions,
    totals: {
      budget: nullableNumber(payload.budget),
      allocated: nullableNumber(payload.totalAllocatedEur, payload.totalAllocated),
      deployedCapital: nullableNumber(payload.deployedCapitalEur, payload.deployedCapital, payload.totalAllocatedEur, payload.totalAllocated),
      deployedCapitalPct: nullableNumber(payload.deployedCapitalPct),
      cash: nullableNumber(payload.cashEur, payload.cash, payload.cashBalance, payload.currentCash, payload.availableCash),
      pnl: nullableNumber(payload.unrealizedPnlEur, payload.unrealizedPnL, payload.unrealizedPnl),
      pnlRealized: nullableNumber(payload.realizedPnlEur, payload.realizedPnL, payload.realizedPnl),
      total: nullableNumber(payload.totalPnlEur, payload.totalPnL, payload.totalPnl),
      openPositions: openCount,
      closedPositions: closedCount,
      currentDrawdownPct: nullableNumber(payload.currentDrawdownPct),
      maxDrawdownPct: nullableNumber(payload.maxDrawdownPct),
      winRate: nullableNumber(payload.winRate),
      hitRate: nullableNumber(payload.hitRate),
    },
    currencyCheck,
    meta: {
      lastUpdate: nullableString(payload.generatedAt || payload.timestamp),
      currency: portfolioCurrency,
      dataStatus: payload.dataStatus || "live",
      twinMaturity: nullableString(payload.twinMaturity),
      priceSource: nullableString(payload.priceSource),
    },
  };
}

/**
 * Central display rule (see problem statement item 7):
 * A valid live portfolio snapshot alone does NOT prove historical
 * performance. Performance/benchmark/alpha/drawdown/win-rate numbers may
 * only be rendered when the portfolio classification is "live" AND the
 * performance data is both real (not a UI placeholder) and complete
 * (no missing series).
 */
export function computeCanDisplayPerformance({ portfolioStatus, performanceDataIsReal, performanceDataIsComplete }) {
  return portfolioStatus === "live" && performanceDataIsReal === true && performanceDataIsComplete === true;
}
